import { useEffect, useRef, useState } from 'react';
import {
  googleMapStyles,
  googleMapsDirectionsUrl,
  hasMapsKey,
  loadGoogleMaps,
  prefersDarkMode,
} from '../lib/maps';
import Icon from './Icon';

/**
 * Approximate pickup area — Google Maps JS with a soft circle (not exact pin).
 */
export default function PickupMap({ lat, lng }) {
  const host = useRef(null);
  const mapRef = useRef(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!hasMapsKey() || lat == null || lng == null) return undefined;
    let cancelled = false;

    loadGoogleMaps()
      .then((maps) => {
        if (cancelled || !host.current) return;
        const center = { lat, lng };
        const dark = prefersDarkMode();
        const map = new maps.Map(host.current, {
          center,
          zoom: 12,
          disableDefaultUI: true,
          zoomControl: true,
          gestureHandling: 'cooperative',
          styles: googleMapStyles(dark),
          clickableIcons: false,
        });
        // Soft area — approximate town center, not exact address
        // eslint-disable-next-line no-new
        new maps.Circle({
          map,
          center,
          radius: 2200,
          strokeColor: dark ? '#5B8DFF' : '#276CFF',
          strokeOpacity: 0.9,
          strokeWeight: 2,
          fillColor: dark ? '#5B8DFF' : '#276CFF',
          fillOpacity: 0.18,
        });
        mapRef.current = map;
      })
      .catch(() => {
        if (!cancelled) {
          setError('Map could not load. Check that Maps JavaScript API is enabled for your key.');
        }
      });

    return () => {
      cancelled = true;
      mapRef.current = null;
    };
  }, [lat, lng]);

  if (!hasMapsKey()) {
    return (
      <div className="map-frame map-frame--fallback">
        <Icon name="map" size={22} />
        <p>Add REACT_APP_GOOGLE_MAPS_API_KEY to show the map.</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="map-frame map-frame--fallback">
        <Icon name="map" size={22} />
        <p>{error}</p>
        <a
          className="text-link"
          href={googleMapsDirectionsUrl(lat, lng)}
          target="_blank"
          rel="noreferrer"
        >
          Open in Google Maps
        </a>
      </div>
    );
  }

  return <div className="map-frame" ref={host} role="img" aria-label="Approximate pickup area map" />;
}
